import { Suspense, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, Outlet, useLocation, useMatches, useNavigate } from 'react-router';

import { PageViews } from '@/features/analytics/PageViews';
import { signInUrl } from '@/features/auth/destination';
import { LanguageSwitcher } from '@/features/settings/LanguageSwitcher';
import { forgetConfirmation } from '@/features/access-control/confirmation';
import { useLogoutMutation } from '@/shared/api/endpoints';
import { selectRole, selectSignedIn, selectUser } from '@/shared/api/sessionSlice';
import { APP_NAME } from '@/shared/lib/brand';
import { cn } from '@/shared/lib/cn';
import { useOnline } from '@/shared/lib/hooks';
import { useContentLocale } from '@/shared/lib/locale';
import { hasSideNavigation } from '@/shared/lib/roles';
import { browser, isElsewhere } from '@/shared/lib/site';
import { linkButton } from '@/shared/ui/Button';
import { Badge, Spinner } from '@/shared/ui/primitives';
import { BottomTabBar, SideNav, TopBar, useDevice } from '@/shared/ui/shell';

import { AppFooter, frontPageUrl } from './AppFooter';
import { HeaderMenu, MenuAction, MenuLink } from './HeaderMenu';
import { activeSection, hrefOf, sectionsFor, splitSections } from './navigation';

type Menu = 'more' | 'account';

const ROLE_TONES = { teacher: 'ok', admin: 'warn', developer: 'info', root: 'bad' } as const;

/**
 * The frame around every page.
 *
 * Which sections there are depends on the account's role: each role has an interface
 * of its own (shared/lib/roles.ts). Within it, each thing has one place. The sections are in the bar (on a phone, in the tabs at
 * the bottom); those a device has no room for are in the "more" menu, and only they.
 * The roles that work in tables (admin, root) have their sections down the side of
 * the page instead of in the bar, and a wide page beside them.
 * What concerns the account (its settings, the language, signing out) is in the
 * account's menu, behind the avatar. A visitor who is not signed in, and a guest (who
 * tries public lessons without an account), have no account block at all: they get
 * what is public (the courses, the terms, the help), the way to sign in, and the
 * choice of language.
 */
export function AppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const signedIn = useSelector(selectSignedIn);
  const user = useSelector(selectUser);
  const online = useOnline();
  const device = useDevice();
  const locale = useContentLocale();
  // A route may ask for a wider page (the course editor does).
  const wide = useMatches().some((match) => (match.handle as { wide?: boolean } | null)?.wide);
  const [logout] = useLogoutMutation();
  const header = useRef<HTMLDivElement>(null);
  // A menu belongs to the page it was opened on: going anywhere closes it.
  const [opened, setOpened] = useState<{ menu: Menu; at: string } | null>(null);
  const open = opened?.at === pathname ? opened.menu : null;
  const close = useCallback(() => setOpened(null), []);
  const toggle = (menu: Menu) => setOpened(open === menu ? null : { menu, at: pathname });

  // Which interface this is: the sections follow the account's one role (a visitor's without one).
  const role = useSelector(selectRole);
  // And a few only with a permission: the accounts, for those root lets manage them.
  const sections = sectionsFor(role, user?.permissions);
  const active = activeSection(sections, pathname);
  // Down the side there is room for every section: nothing is left for a menu.
  const side = hasSideNavigation(role) && device !== 'mobile';
  const { shown, more } = side ? { shown: sections, more: [] } : splitSections(sections, device);
  const activeLabel = sections.find((section) => section.id === active)?.label;
  const go = (id: string) => {
    const section = sections.find((candidate) => candidate.id === id);
    if (!section) return;
    // A section of another part of the installation is on another host.
    const href = hrefOf(section);
    if (isElsewhere(href)) browser.leave(href);
    else void navigate(href);
  };

  return (
    <div className="flex min-h-dvh flex-col">
      <PageViews />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-40 focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2"
      >
        {t('nav.skip')}
      </a>
      <div ref={header} className="sticky top-0 z-20">
        <TopBar
          brand={APP_NAME}
          // The logo leads to the front page, a static page per language (frontend/site).
          brandHref={frontPageUrl(locale)}
          brandLabel={t('nav.frontPage', { app: APP_NAME })}
          links={side ? [] : sections.map((section) => ({ id: section.id, label: t(section.label), href: hrefOf(section) }))}
          title={side && activeLabel ? t(activeLabel) : undefined}
          activeId={active}
          onNavigate={go}
          navLabel={t('nav.main')}
          // Whose interface this is, for everyone but a student, whose it is by default.
          badge={role && role !== 'student' ? <Badge tone={ROLE_TONES[role]}>{t(`roles.${role}`)}</Badge> : undefined}
          // Only when the device leaves sections out: an empty menu is not offered.
          onMenu={more.length > 0 ? () => toggle('more') : undefined}
          menuLabel={t('nav.more')}
          menuIcon="menu"
          menuOpen={open === 'more'}
          // A visitor has no account to show: the way in, and the language they may need to read it.
          account={signedIn}
          actions={
            signedIn ? undefined : (
              <div className="flex items-center gap-2">
                {pathname === '/login' || pathname === '/register' ? null : (
                  <a href={signInUrl(location)} className={linkButton('secondary')}>
                    {t('nav.signIn')}
                  </a>
                )}
                {/* A phone has room for the language's code, not its name. */}
                <LanguageSwitcher compact={device === 'mobile'} />
              </div>
            )
          }
          onAccount={() => toggle('account')}
          accountLabel={t('nav.account')}
          accountOpen={open === 'account'}
        />
        {open === 'more' && more.length > 0 ? (
          <HeaderMenu label={t('nav.more')} header={header} onClose={close}>
            <nav aria-label={t('nav.more')} className="flex flex-col gap-1">
              {more.map((section) => (
                <MenuLink key={section.id} to={hrefOf(section)} icon={section.icon} onClick={close}>
                  {t(section.label)}
                </MenuLink>
              ))}
            </nav>
          </HeaderMenu>
        ) : null}
        {open === 'account' && signedIn ? (
          <HeaderMenu label={t('nav.account')} header={header} onClose={close}>
            {user ? (
              <p className="px-4 pt-1 pb-2">
                <span className="block truncate text-sm font-bold">{user.displayName}</span>
                <span className="block truncate text-xs text-muted">
                  {user.email} · {t(`roles.${user.role}`)}
                </span>
              </p>
            ) : null}
            <MenuLink to="/settings" icon="tune" onClick={close}>
              {t('nav.settings')}
            </MenuLink>
            <div className="px-1 py-1">
              <LanguageSwitcher fullWidth />
            </div>
            <MenuAction
              icon="logout"
              onClick={() => {
                close();
                forgetConfirmation();
                void logout();
              }}
            >
              {t('nav.logout')}
            </MenuAction>
          </HeaderMenu>
        ) : null}
        {online ? null : (
          <p role="status" className="bg-warn-soft px-4 py-2 text-center text-sm text-warn">
            {t('offline')}
          </p>
        )}
      </div>
      <div className="flex flex-1">
        {side ? (
          // Stays in view under the bar (64 px) while the page scrolls.
          <aside className="sticky top-16 h-[calc(100dvh-4rem)] w-56 shrink-0 self-start overflow-y-auto lg:w-60">
            <SideNav
              label={t('nav.main')}
              items={sections.map((section) => ({ id: section.id, label: t(section.label), icon: section.icon, href: hrefOf(section) }))}
              activeId={active}
              onSelect={go}
            />
          </aside>
        ) : null}
        <main
          id="main"
          className={cn(
            // min-w-0: a table wider than the page scrolls inside itself, not the page.
            'mx-auto w-full min-w-0 flex-1 px-4 py-6 sm:px-6 sm:py-8',
            wide || side ? 'max-w-7xl' : 'max-w-4xl',
          )}
        >
          <Suspense fallback={<Spinner label="…" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      {/* Room under it for the tab bar, which lies over the bottom of the page on a phone.
          On a wrapper: the kit's footer sets its own padding, which a utility does not override. */}
      <div data-testid="foot" className={cn(device === 'mobile' && sections.length > 0 && 'pb-24')}>
        <AppFooter
          actions={
            pathname === '/feedback' ? null : (
              // The page it was opened from goes with the message: where to look.
              <Link
                to={`/feedback?from=${encodeURIComponent(window.location.href)}`}
                className={linkButton('secondary', 'sm')}
              >
                {t('footer.report')}
              </Link>
            )
          }
        />
      </div>
      {device === 'mobile' && sections.length > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-20">
          <BottomTabBar
            label={t('nav.main')}
            items={shown.map((section) => ({ id: section.id, label: t(section.label), icon: section.icon, href: hrefOf(section) }))}
            activeId={active}
            onSelect={go}
          />
        </div>
      ) : null}
    </div>
  );
}
